import { env } from './env';
import { getAccessToken, supabase } from './supabase';

/** Error body shape produced by the backend HttpExceptionFilter. */
interface ApiErrorBody {
  statusCode?: number;
  message?: string | string[];
  error?: string;
  path?: string;
  timestamp?: string;
}

export class ApiError extends Error {
  readonly statusCode: number;
  readonly messages: string[];
  readonly path?: string;

  constructor(statusCode: number, messages: string[], path?: string) {
    super(messages[0] ?? `Yêu cầu thất bại (${statusCode})`);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.messages = messages;
    this.path = path;
  }

  /** Session missing, invalid or expired — the caller should sign in again. */
  get isUnauthorized(): boolean {
    return this.statusCode === 401;
  }

  /** Signed in, but lacking ownership or the ADMIN role. */
  get isForbidden(): boolean {
    return this.statusCode === 403;
  }
}

type UnauthorizedListener = () => void;
const unauthorizedListeners = new Set<UnauthorizedListener>();

/**
 * Called after a request fails with 401 twice (i.e. a token refresh did not
 * help). The auth layer uses this to drop the session and route to sign-in.
 */
export function onUnauthorized(listener: UnauthorizedListener): () => void {
  unauthorizedListeners.add(listener);
  return () => unauthorizedListeners.delete(listener);
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  /** Attach the Supabase access token. `'required'` fails fast without one. */
  auth?: 'none' | 'optional' | 'required';
  query?: Record<string, string | number | boolean | undefined>;
  signal?: AbortSignal;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${env.apiBaseUrl}${path.startsWith('/') ? path : `/${path}`}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody = {};
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // Non-JSON error (proxy, gateway, network appliance). Fall through.
  }
  const raw = body.message ?? body.error ?? response.statusText;
  const messages = Array.isArray(raw) ? raw : [String(raw)];
  return new ApiError(body.statusCode ?? response.status, messages, body.path);
}

async function send(url: string, options: RequestOptions, token?: string): Promise<Response> {
  const headers: Record<string, string> = { Accept: 'application/json' };
  const formBody = typeof FormData !== 'undefined' && options.body instanceof FormData;
  if (options.body !== undefined && !formBody) headers['Content-Type'] = 'application/json';
  if (token) headers.Authorization = `Bearer ${token}`;
  const body: BodyInit | undefined =
    options.body === undefined
      ? undefined
      : formBody
        ? (options.body as FormData)
        : JSON.stringify(options.body);

  return fetch(url, {
    method: options.method ?? 'GET',
    headers,
    body,
    signal: options.signal,
  });
}

/**
 * Single entry point for every backend call.
 *
 * A 401 triggers one Supabase token refresh and one retry; if that still fails
 * the `onUnauthorized` listeners run so the app can send the user to sign-in.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const auth = options.auth ?? 'none';
  const url = buildUrl(path, options.query);

  let token = auth === 'none' ? undefined : await getAccessToken();
  if (auth === 'required' && !token) {
    const error = new ApiError(401, ['Bạn cần đăng nhập để tiếp tục.'], path);
    unauthorizedListeners.forEach((listener) => listener());
    throw error;
  }

  let response = await send(url, options, token);

  if (response.status === 401 && auth !== 'none' && supabase) {
    const { data } = await supabase.auth.refreshSession();
    token = data.session?.access_token;
    if (token) response = await send(url, options, token);
  }

  if (response.status === 401 && auth !== 'none') {
    unauthorizedListeners.forEach((listener) => listener());
  }

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}
